import { isAxiosError } from 'axios';
import { ScanQrCode } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import CardAccountBank from '@/domains/mobile-web/components/CardAccountBank/CardAccountBank';
import CardCommision from '@/domains/mobile-web/components/CardCommision/CardCommision';
import CardTautan from '@/domains/mobile-web/components/CardTautan/CardTautan';
import InfoNote from '@/domains/mobile-web/components/InfoNote/InfoNote';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useAffiliate } from '@/domains/mobile-web/hooks/useAffiliate';
import { usePayoutAccounts } from '@/domains/mobile-web/hooks/useBanks';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

const QR_SIZE = 200;

const loadErrorText = (error: Error) =>
  isAxiosError(error) && error.response?.status === 403
    ? 'Halaman affiliate khusus untuk Family Member.'
    : isAxiosError(error) && error.response?.status === 404
      ? 'Akun affiliate tidak ditemukan.'
      : 'Gagal memuat data affiliate.';

const Affiliate = () => {
  const navigate = useNavigate();
  const affiliateQuery = useAffiliate();
  const accountsQuery = usePayoutAccounts();
  const affiliate = affiliateQuery.data;
  // Newest first; the latest one added is where withdrawals go
  const account = accountsQuery.data?.rows[0];
  const isLoading = affiliateQuery.isLoading || accountsQuery.isLoading;
  const error = affiliateQuery.error ?? accountsQuery.error;
  // Null unless the affiliate account is active; usable only once a payout account is saved
  const link = (account && affiliate?.referral_link) || undefined;
  const addAccount = () => navigate('/store/bank');

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader type="back" title="Affiliate" onBack={() => navigate(-1)} />
      </HeaderMobile>

      <WrapperMobile>
        {!affiliate || !accountsQuery.data ? (
          <p className="reg12 text-text-grey text-center py-6">
            {isLoading
              ? 'Memuat...'
              : error
                ? loadErrorText(error)
                : 'Gagal memuat data affiliate.'}
          </p>
        ) : (
          <>
            <CardCommision
              income={Number(affiliate.commission_total)}
              clickTotal={affiliate.click_total}
              unpaidCommission={Number(affiliate.available_balance)}
              onHistory={() => navigate('/store/history-affiliate')}
            />
            <CardAccountBank
              showBankAccount={Boolean(account)}
              bankCode={account?.bank_code}
              bankName={account ? (account.bank_name ?? account.bank_code) : undefined}
              accountNumber={account?.account_number}
              name={account?.account_holder ?? undefined}
              onEdit={addAccount}
              onAddAccount={addAccount}
            />
            <CardTautan
              linkAffiliate={link}
              placeholder={
                !account ? 'Tautan affiliate belum tersedia' : 'Tautan affiliate tidak aktif.'
              }
              note={
                !account ? 'Tambahkan rekening tujuan sebelum tautan dapat digunakan.' : undefined
              }
            />

            <div className="shadowCard p-3 flex flex-col gap-3">
              <p className="title2medium">QR Code</p>
              <div className="border flex flex-col gap-3 text-center border-blue-cs-200 bg-text-blue-10 items-center justify-center p-3">
                {link ? (
                  <>
                    <QRCodeCanvas
                      value={link}
                      size={QR_SIZE}
                      marginSize={2}
                      title={link}
                      aria-label="QR code affiliate"
                    />
                    <p className="body1">Format QR beresolusi dengan tautan langsung</p>
                  </>
                ) : (
                  <>
                    <ScanQrCode size={56} strokeWidth={1.5} />
                    <p className="body1">
                      {!account ? 'QR Code belum tersedia' : 'Tautan affiliate tidak aktif.'}
                    </p>
                  </>
                )}
              </div>
              {!account && (
                <InfoNote>Tambahkan rekening tujuan sebelum QR code dapat digunakan.</InfoNote>
              )}
            </div>
          </>
        )}
      </WrapperMobile>
    </main>
  );
};

export default Affiliate;
